import React, { useEffect, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  PatientContext,
  PreferredLanguageContext,
  UserContext,
} from "../../lib/main-context";
import customizeMessages from "../../lib/customizeMessages";

import format from "date-fns/format";

import OnboardingStepHeader from "./OnboardingStepHeader";
import Sallie from "../UIComponents/Sallie";
import CategorySelector from "../UIComponents/CategorySelector";
import SubmitButton from "../UIComponents/SubmitButton";

import brainA from "../../images/brain-a.jpg";
import brainB from "../../images/brain-b.jpg";

import {
  getTBISymptoms,
  postRegisterFactors,
  postNewResultingFactors,
  getTbiDate,
  getStrokeDate,
  getNonTbiDate,
  addSymptomDetails,
} from "../../api/TBIRequests";

import {
  MEDICAL_SYMPTOMS,
  SALLIE_BRAIN,
  SYMPTOM_SELECTOR,
  SAVE_AND_CONTINUE,
} from "../../constants/OnboardingTranslation";

const SelectLaterSymptoms = ({ setOnboardingPercent }) => {
  const history = useHistory();
  const location = useLocation();

  const { user } = useContext(UserContext);
  const { patient } = useContext(PatientContext);
  const { language } = useContext(PreferredLanguageContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [returnedData, setReturnedData] = useState([]);
  const [selectedItems, setSelectedItems] = useState({})
  const [dateOfTBI, setDateOfTBI] = useState(undefined);
  const [headacheParts, setHeadacheParts] = useState([]);

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);

  // fetch symptom date if lost when user didn't finish onboarding steps
  useEffect(() => {
    if (location?.state?.date === undefined) {
      if (patient.patientType === "Stroke") {
        getStrokeDate(patient.patientId)
          .then((res) => {
            let TbiDate = format(
              new Date(res.data.stroke_incident_date),
              "MM-dd-yyyy"
            );
            setDateOfTBI(TbiDate);
          })
          .catch((e) => {
            history.push("/oops");
          });
      } else if (
        patient.patientType !== "TBI/Concussion" &&
        patient.patientType !== "hasSymptom" &&
        patient.patientType !== "Post-Concussive Syndrome" &&
        patient.patientType !== "tbiPatient"
      ) {
        getNonTbiDate(patient.patientId)
          .then((res) => {
            let symptomDate = format(
              new Date(res.data.symptoms_date),
              "MM-dd-yyyy"
            );
            setDateOfTBI(symptomDate);
          })
          .catch((error) => {
            console.error("Error fetching non-TBI date:", error);
            history.push("/oops");
          });
      } else {
        getTbiDate(patient.patientId)
          .then((res) => {
            let TbiDate = format(
              new Date(res.data.tbi_incident_date),
              "MM-dd-yyyy"
            );
            setDateOfTBI(TbiDate);
          })
          .catch(() => {
            history.push("/oops");
          });
      }
    } else {
      setDateOfTBI(format(new Date(location?.state?.date), "MM-dd-yyyy"));
    }
  }, []);

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(60)
      : setOnboardingPercent(40);

    const fetchSymptoms = async () => {
      try {
        const res = await getTBISymptoms();

        const processedList = categories.map((category) => {
          const items = res.data
            .filter((item) => item.subcategory === category)
            .map((item) => item.factor);

          return { category, items };
        })

        setReturnedData(processedList);
        setLoading(false);
      } catch (err) {
        console.error("fetchSymptoms error:", err)
        history.push("/oops")
      }
    };

    fetchSymptoms();
  }, []);

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: MEDICAL_SYMPTOMS[language],
      tbiPatient: MEDICAL_SYMPTOMS[language],
      caregiver: MEDICAL_SYMPTOMS[language],
    },
    sallieText: {
      Stroke: SALLIE_BRAIN[language].stroke,
      tbiPatient: SALLIE_BRAIN[language].tbi,
      caregiver: SALLIE_BRAIN[language].caregiver,
    },
    noSymptomsText: {
      Stroke: SYMPTOM_SELECTOR[language].errors.patient,
      tbiPatient: SYMPTOM_SELECTOR[language].errors.patient,
      caregiver: SYMPTOM_SELECTOR[language].errors.caregiver,
    },
    selectHeadacheLocationText: {
      Stroke: SYMPTOM_SELECTOR[language].errors.headache_location,
      tbiPatient: SYMPTOM_SELECTOR[language].errors.headache_location,
      caregiver: SYMPTOM_SELECTOR[language].errors.headache_location,
    },
  });
  const getMessage = customizeMessages({ user, getPossibleMessages });

  const categories = [
    "cognitive",
    "sleep",
    "emotional",
    "physical",
    "speech",
    "vision",
  ];

  const colors = {
    cognitive: "#91a5eb",
    sleep: "#f5b9bb",
    physical: "#f5c389",
    emotional: "#fbec5d",
    speech: "#a7d69f",
    vision: "#9be0da",
  };

  const handlePrev = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex((prev) => prev - 1);
    }
  };

  const handleNextOrSubmit = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const processDataForSending = () => {
    return Object.entries(selectedItems).flatMap(([category, itemList]) => {
      return itemList.map((item) => ({
        factor: item,
        category: "medical",
        subcategory: category,
        datetimeOfSymptom: dateOfTBI,
        hadSymptom: true,
      }));
    });
  };

  const handleSubmit = () => {
    setSubmitting(true);
    let processedData = processDataForSending();
    try {
      if (processedData.length === 0) {
        setError(SYMPTOM_SELECTOR[language].errors.no_symptom.neutral);
        setSubmitting(false);
        return;
      }

      if (selectedItems?.vision.includes("Headaches caused by close work: computers, reading, gaming")) {
        if (headacheParts.length > 0) {
          addSymptomDetails({
            patientId: patient.patientId,
            data: {
              symptom:
                "Headaches caused by close work: computers, reading, gaming",
              detail: headacheParts,
            },
          });
        } else {
          setError(SYMPTOM_SELECTOR[language].errors.headache_location)
          setSubmitting(false);
          return;
        }
      }

      postRegisterFactors({
        patientId: patient.patientId,
        data: processedData,
      });

      postNewResultingFactors({
        patientId: patient.patientId,
        data: processedData,
      }).then(() => {
        {
          //add more symptoms,if there's no tracking factors
          location?.state?.fromPatientDashboard
            ? history.push("/selectTopSymptoms", {
              forwardData: { selectedItems, processedData, dateOfTBI },
              fromPatientControl: location.state?.fromPatientControl,
              fromPatientDashboard: true,
            })
            : history.push("/selectTopSymptoms", {
              forwardData: { selectedItems, processedData, dateOfTBI },
              fromPatientControl: location.state?.fromPatientControl,
            });
        }
      });
    } catch (err) {
      console.log("some error in SelectLaterSymptoms handleSubmit");
      console.log(err);
      history.push("/oops");
    }
  };

  return (
    <>
      {loading ? (
        "Loading..."
      ) : (
        <>
          <div className="flex flex-col items-center">
            <div className='w-2/3'>
              <OnboardingStepHeader text={getMessage("mainHeader")} />
              <Sallie text={getMessage("sallieText")} />
              <div className='flex flex-col md:flex-row gap-8 my-8'>
                <img src={brainA} className="inline-block h-auto w-full" />
                <img src={brainB} className="inline-block h-auto w-full" />
              </div>
            </div>
          </div>

          <h5 className='text-center mb-8'>
            {SYMPTOM_SELECTOR[language].category_header}
          </h5>

          <div className='flex flex-col items-center w-full md:w-2/3 mx-auto gap-8'>
            <div className='flex flex-col w-full gap-2'>
              <CategorySelector
                categoryType="Symptoms"
                categorizedItemList={returnedData}
                setCategorizedItemList={setReturnedData}
                colors={colors}
                currentCategoryIndex={currentCategoryIndex}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                setError={setError}
                headacheParts={headacheParts}
                setHeadacheParts={setHeadacheParts}
              />
              {error &&
                <p className='text-red-500'>{error}</p>
              }
            </div>
            <div className='flex justify-between w-full'>
              <SubmitButton
                type="button"
                onClick={handlePrev}
                disabled={currentCategoryIndex === 0}
                greyout={currentCategoryIndex === 0}
              >
                {SYMPTOM_SELECTOR[language].previous_category}
              </SubmitButton>
              <SubmitButton
                type="button"
                onClick={handleNextOrSubmit}
                disabled={submitting}
              >
                {currentCategoryIndex < categories.length - 1
                  ? SYMPTOM_SELECTOR[language].next_category
                  : `${SAVE_AND_CONTINUE[language]} >`}
              </SubmitButton>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SelectLaterSymptoms;
